import { IonButtons, IonContent, IonHeader, IonMenuButton, IonPage, IonTitle, IonToolbar, IonListHeader, IonList, IonItem, IonLabel, IonIcon, IonButton, IonAlert } from '@ionic/react';
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectLog, removeTrip, selectSum, selectMode } from '../features/log/logSlice';
import { TripType } from '../features/log/tripType';
import { trashBinOutline, trashBinSharp } from 'ionicons/icons';
import { AddLogEntry } from '../features/log/AddLogEntry';
import { removeLogEntryFromDatabase } from '../utils/airtable-api';
import { milesOrPoints } from '../app/miles-or-points';
import { ACTIVITIES } from '../features/log/activities';
import { LogEntry } from '../features/log/logEntry';

const Log: React.FC = () => {
  const dispatch = useDispatch(),
    log = useSelector(selectLog),
    sum = useSelector(selectSum),
    mode = useSelector(selectMode),
    [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false),
    [deleteTripIndex, setDeleteTripIndex] = useState(0),
    [deleteTripEntry, setDeleteTripEntry] = useState<LogEntry | null>(null);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Travel Log</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonListHeader><h2>Log Your Walk</h2></IonListHeader>
        <div className="ion-padding">
          <AddLogEntry/>
        </div>
        {log?.entries?.length > 0 && 
          <React.Fragment>
            <IonListHeader>
              <h2>
                Your Travel Log
                { sum > 0 && <span> ({sum.toFixed(2)} {milesOrPoints(mode, sum ?? 0)})</span>}
              </h2>
            </IonListHeader>
            <IonList>
              {log.entries.map((entry, ii) => 
                <IonItem key={ii}>
                  <IonLabel>
                    <h3>{new Date(Date.parse(entry.date)).toLocaleDateString()}</h3>
                    <h4>{Math.round(entry.distance * 10) / 10} {mode === 'miles' ? 'mile' : milesOrPoints(mode, entry.distance)}{mode === 'points' && ': '} <TripTypeLabel type={entry.type}/> {entry.leg && <span>— {entry.leg}</span>}</h4>
                    {entry.notes && <p>{entry.notes}</p>}
                  </IonLabel>
                  <IonButtons slot="end">
                    <IonButton onClick={() => {
                      setShowDeleteConfirmation(true);
                      setDeleteTripIndex(ii);
                      setDeleteTripEntry(entry);
                    }}>
                      <IonIcon slot="icon-only" ios={trashBinOutline} md={trashBinSharp}></IonIcon>
                    </IonButton>
                  </IonButtons>
                </IonItem>
              )}
            </IonList>
          </React.Fragment>}
      </IonContent>
      <IonAlert isOpen={showDeleteConfirmation}
        header="Confirm Deletion"
        message={`Are you sure you want to delete this ${deleteTripEntry?.distance}-${milesOrPoints(mode, deleteTripEntry?.distance ?? 0)} ${tripTypeLabel(deleteTripEntry?.type ?? TripType.Walk)}?`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel'
          },
          {
            text: 'Delete',
            role: 'destructive',
            handler: () => {
              dispatch(removeTrip(deleteTripIndex));
              removeLogEntryFromDatabase(deleteTripEntry?.id);
              setShowDeleteConfirmation(false);
            }
          }
        ]}
      />
    </IonPage>
  );
};

type TripTypeLabelProps = { type: TripType };

export const TripTypeLabel = ({ type } : TripTypeLabelProps) => <span>{tripTypeLabel(type)}</span>

const tripTypeLabel = (type: TripType) => {
  switch(type) {
    case TripType.Walk:
      return "Walk";
    case TripType.Bus:
      return "Bus Ride";
    default:
      const activity = ACTIVITIES.find(a => a?.id === type);
      return activity?.short_name ?? activity?.name ?? 'Trip';
  }
}

export default Log;